import { Component, effect, OnDestroy, OnInit } from '@angular/core';
import { AuthenticationService, QuotationCartService } from '@app/_services';
import { BsModalService, BsModalRef } from 'ngx-bootstrap/modal';
import { ConfirmDialogComponent } from '@app/shared/components/confirm-dialog/confirm-dialog.component';
import { Router, RouterLinkActive, RouterLink, RouterOutlet } from '@angular/router';
import { SiteService } from '@app/_services/site.service';
import { TranslateModule } from '@ngx-translate/core';
import { MatIconModule } from '@angular/material/icon';
import { NgFor, NgIf } from '@angular/common';
import { listGroup, listItem } from '@app/_components/root/general-navigation/list-items.interface';
import { GeneralNavigationService } from '@app/_components/root/general-navigation/general-navigation.service';
import { DeviceDetectorService } from 'ngx-device-detector';

@Component({
    selector: 'app-layout',
    templateUrl: './layout.component.html',
    styleUrls: ['./layout.component.scss'],
    standalone: true,
    imports: [
        NgFor,
        RouterLinkActive,
        NgIf,
        MatIconModule,
        RouterLink,
        RouterOutlet,
        TranslateModule
    ]
})
export class LayoutComponent implements OnInit, OnDestroy {
    links: { label: string; url: string; icon: string }[] = [
        { label: 'dashboard', url: './dashboard', icon: 'dashboard' },
        { label: 'edit_profile', url: './profile', icon: 'edit-profile' },
        { label: 'order_history', url: './orders', icon: 'order-history' },
        { label: 'bookings', url: './bookings', icon: 'booking' },
        //{ label: 'Loyalty Points', url: './loyalty' },
        ...(this.siteService.features?.vouchers
            ? [{ label: 'vouchers', url: './vouchers', icon: 'vouchers' }]
            : []),
        { label: 'addresses', url: './addresses', icon: 'addresses' },
        { label: 'password', url: './password', icon: 'password' },
        { label: 'messages', url: './messages', icon: 'messages' },
        ...(this.siteService.features?.chat
            ? [{ label: 'live_chat', url: './live-chat', icon: 'live-chat' }]
            : []),
        { label: 'logout', url: '', icon: 'logout' }
    ];
    quotationsLinks: { label: string; url: string; icon: string }[] = [
        ...(this.quotationCartService.quantity
            ? [
                  {
                      label: 'draft_quotation',
                      url: './quotation-drafts',
                      icon: 'active-quotation'
                  }
              ]
            : []),
        {
            label: 'active_quotation',
            url: './active-quotations',
            icon: 'active-quotation'
        },
        {
            label: 'quotation_history',
            url: './quotations',
            icon: 'order-history'
        }
    ];
    quotationRequestsLinks: { label: string; url: string; icon: string }[] = [
        {
            label: 'quotation_requests',
            url: './quotation-requests',
            icon: 'active-quotation'
        }
    ];
    propertyLinks: { label: string; url: string }[] = this.siteService.features
        ?.propertyManagement && [
        { label: 'active_properties', url: './property' }
    ];
    ownerPropertyLinks: { label: string; url: string }[] = this.siteService
        .features?.propertyManagement && [
        { label: 'active_properties', url: './ownerProperty' }
    ];

    public navigationItems: listGroup[] = [];
    /*   ...(this.siteService.features?.quotations) ? [{
              label: 'quotations',
              items: [
                  ...(this.quotationCartService.quantity ? [{
                      label: 'draft_quotation',
                      icon: 'active-quotation',
                      iconType: 'svg-icon',
                      routerLink: './account/quotation-drafts'
                  }] : []),
                  {
                      label: 'active_quotation',
                      icon: 'active-quotation',
                      iconType: 'svg-icon',
                      routerLink: './account/active-quotations'
                  },
                  {
                      label: 'quotation_history',
                      icon: 'order-history',
                      iconType: 'svg-icon',
                      routerLink: './account/quotations'
                  }
              ]
          } as listGroup] : [] */

    userDetails = this.authService.currentUserValue;
    bsModalRef: BsModalRef;
    quantity;

    public showGeneralNavMenu: boolean;

    constructor(
        private authService: AuthenticationService,
        private modalService: BsModalService,
        private router: Router,
        private quotationCartService: QuotationCartService,
        public siteService: SiteService,
        private generalNavigationService: GeneralNavigationService,
        private deviceDetector: DeviceDetectorService
    ) {
        effect(() => {
            this.showGeneralNavMenu = this.generalNavigationService.showMenu;
        });
    }

    ngOnInit(): void {
        this.quotationCartService.quantity$.subscribe(quantity => {
            this.quantity = quantity;
        });

        // Rebuild navigation items based on user type
        const currentUser = this.authService.currentUserValue;
        const isCustomer = currentUser?.type === 'C';

        this.navigationItems = [
            {
                items: [
                    {
                        label: 'dashboard',
                        icon: 'dashboard',
                        iconType: 'svg-icon',
                        routerLink: './account/dashboard'
                    },
                    {
                        label: 'edit_profile',
                        icon: 'edit-profile',
                        iconType: 'svg-icon',
                        routerLink: './account/profile'
                    },
                    ...(isCustomer
                        ? [
                              {
                                  label: 'subscription',
                                  icon: 'credit_card',
                                  iconType: 'material',
                                  routerLink: './account/subscription'
                              } as listItem,
                              {
                                  label: 'ai_tokens',
                                  icon: 'token',
                                  iconType: 'material',
                                  routerLink: './account/ai-tokens'
                              } as listItem
                          ]
                        : []),
                    {
                        label: 'order_history',
                        icon: 'order-history',
                        iconType: 'svg-icon',
                        routerLink: './account/orders'
                    },
                    {
                        label: 'bookings',
                        icon: 'booking',
                        iconType: 'svg-icon',
                        routerLink: './account/bookings'
                    },
                    ...(this.siteService.features?.vouchers
                        ? [
                              {
                                  label: 'vouchers',
                                  icon: 'vouchers',
                                  iconType: 'svg-icon',
                                  routerLink: './account/vouchers'
                              } as listItem
                          ]
                        : []),
                    {
                        label: 'addresses',
                        icon: 'addresses',
                        iconType: 'svg-icon',
                        routerLink: './account/addresses'
                    },
                    {
                        label: 'password',
                        icon: 'password',
                        iconType: 'svg-icon',
                        routerLink: './account/password'
                    },
                    {
                        label: 'messages',
                        icon: 'messages',
                        iconType: 'svg-icon',
                        routerLink: './account/messages'
                    },
                    ...(this.siteService.features?.chat
                        ? [
                              {
                                  label: 'live_chat',
                                  icon: 'live-chat',
                                  iconType: 'svg-icon',
                                  routerLink: './account/live-chat'
                              } as listItem
                          ]
                        : []),
                    {
                        label: 'logout',
                        icon: 'logout',
                        iconType: 'svg-icon',
                        click: () => {
                            this.logout();
                        }
                    }
                ]
            }
        ];

        this.generalNavigationService.menuItems = this.navigationItems;
        this.generalNavigationService.showMenu = !this.deviceDetector.isMobile();
    }

    ngOnDestroy(): void {
        this.generalNavigationService.menuState = false;
        this.generalNavigationService.menuItems = [];
    }

    public toggleNavigation() {
        this.generalNavigationService.toggleState();
    }

    logout() {
        this.bsModalRef = this.modalService.show(ConfirmDialogComponent, {
            class: 'modal-sm'
        });
        this.bsModalRef.content.onClose.subscribe(confirmed => {
            if (confirmed) {
                this.authService.logout().subscribe(res => {
                    res;
                });
                this.router.navigate(['/']);
            }
        });
    }
}
